import { Fragment, useEffect, useState } from 'react'
import { Link, NavLink, useSearchParams } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { getCompany } from '../../api/settings'
import { HelpTip } from '../../components/HelpTip'
import { companyNow, fmtDay, localToday } from '../../utils/format'
import { money } from '../sales/salesShared'

// Pieces shared by the Reports pages: tabs, periods, and the statement
// table that the income statement and balance sheet are drawn with.

export function ReportsTabs() {
  const tabs = [
    { to: '/accounting/reports', label: 'Overview', end: true },
    { to: '/accounting/reports/income-statement', label: 'Income statement' },
    { to: '/accounting/reports/balance-sheet', label: 'Balance sheet' },
    { to: '/accounting/reports/trial-balance', label: 'Trial balance' },
    { to: '/accounting/reports/general-ledger', label: 'General ledger' },
    { to: '/accounting/reports/ap-subledger', label: 'Payables subledger' },
    { to: '/accounting/reports/ar-subledger', label: 'Receivables subledger' },
  ]
  return (
    <nav className="section-tabs fs-noprint" aria-label="Reports">
      {tabs.map(({ to, label, end }) => <NavLink key={to} to={to} end={end}>{label}</NavLink>)}
    </nav>
  )
}

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const PERIODS = [
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'this_quarter', label: 'This quarter' },
  { id: 'last_quarter', label: 'Last quarter' },
  { id: 'ytd', label: 'This year to date' },
  { id: 'last_year', label: 'Last year' },
  { id: 'custom', label: 'Custom dates' },
]

/**
 * The report period kept in the URL: ?period=this_month, or period=custom
 * with start and end. A link with only start/end counts as custom.
 */
export function usePeriodParams(fallback = 'this_month') {
  const [params, setParams] = useSearchParams()
  const set = (patch) => setParams((p) => {
    const n = new URLSearchParams(p)
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)))
    return n
  }, { replace: true })
  const raw = params.get('period')
  const period = PERIODS.some((x) => x.id === raw) ? raw : (params.get('start') || params.get('end') ? 'custom' : fallback)
  const [ps, pe] = periodDates(period === 'custom' ? fallback : period)
  const start = period === 'custom' ? (params.get('start') || ps) : ps
  const end = period === 'custom' ? (params.get('end') || pe) : pe
  const setPeriod = (id) => set(id === 'custom' ? { period: id, start, end } : { period: id, start: '', end: '' })
  return { params, set, period, start, end, setPeriod }
}

/** Period choice plus the two dates when it's custom. */
export function PeriodPicker({ period, start, end, setPeriod, set }) {
  return (
    <>
      <select className="ci-select" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period">
        {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
      {period === 'custom' && (
        <>
          <input className="ci-select" type="date" value={start} onChange={(e) => set({ period: 'custom', start: e.target.value, end })} aria-label="From" />
          <input className="ci-select" type="date" value={end} onChange={(e) => set({ period: 'custom', start, end: e.target.value })} aria-label="To" />
        </>
      )}
    </>
  )
}

/** Rows to a CSV download. */
export function downloadCsv(name, rows) {
  const csv = rows.map((a) => a.map((x) => `"${String(x ?? '').replaceAll('"', '""')}"`).join(',')).join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `${name}_${localToday()}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/** [start, end] for a period preset, as YYYY-MM-DD on the company's clock. */
export function periodDates(id, today = companyNow()) {
  const y = today.getFullYear()
  const m = today.getMonth()
  const q = Math.floor(m / 3) * 3
  switch (id) {
    case 'last_month': return [ymd(new Date(y, m - 1, 1)), ymd(new Date(y, m, 0))]
    case 'this_quarter': return [ymd(new Date(y, q, 1)), ymd(new Date(y, q + 3, 0))]
    case 'last_quarter': return [ymd(new Date(y, q - 3, 1)), ymd(new Date(y, q, 0))]
    case 'ytd': return [ymd(new Date(y, 0, 1)), ymd(today)]
    case 'last_year': return [ymd(new Date(y - 1, 0, 1)), ymd(new Date(y - 1, 11, 31))]
    default: return [ymd(new Date(y, m, 1)), ymd(new Date(y, m + 1, 0))]
  }
}

/** The general ledger for an account (and the accounts under it). With no
 * start, it runs from the start of the end date's year. */
export const glHref = (code, start, end) => {
  const p = new URLSearchParams({ account: code })
  if (end) {
    p.set('period', 'custom')
    p.set('start', start || `${end.slice(0, 4)}-01-01`)
    p.set('end', end)
  }
  return `/accounting/reports/general-ledger?${p}`
}

/** Money the way statements show it: negatives in brackets. */
export const acct = (v) => (v == null ? '' : (Number(v) < -0.004 ? `(${money(-Number(v))})` : money(v)))

/** "1 January 2026 to 31 March 2026" / "as of 31 March 2026". */
export const periodLabel = (start, end) => `${fmtDay(start)} to ${fmtDay(end)}`

/** The company's name for statement headings. */
export function useCompanyName() {
  const [name, setName] = useState('')
  useEffect(() => { getCompany().then((c) => setName(c?.company_name || '')).catch(() => {}) }, [])
  return name
}

/** Statement heading: company, statement, period. */
export function StatementHead({ company, title, sub }) {
  return (
    <div className="fs-head">
      {company && <div className="fs-company">{company}</div>}
      <div className="fs-title">{title}</div>
      <div className="fs-sub">{sub}</div>
    </div>
  )
}

/**
 * The statement table. rows: [{ kind, label, amount, prior, help, k, accounts, link }]
 *   kind: 'heading' | 'subheading' | 'line' | 'subtotal' | 'total' | 'note'
 *   accounts: detail rows under a line, shown when it's opened.
 */
export function StatementTable({ rows, priorLabel, currentLabel, openAll, glRange }) {
  const [open, setOpen] = useState({})
  const showPrior = !!priorLabel
  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }))
  const glLink = (code) => code && glHref(code, glRange?.[0], glRange?.[1])
  return (
    <table className="fs-table">
      <thead>
        <tr>
          <th />
          <th className="num">{currentLabel}</th>
          {showPrior && <th className="num">{priorLabel}</th>}
          {showPrior && <th className="num fs-change">Change</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const key = `${r.kind}-${r.label}-${i}`
          const help = (r.k || r.help) && <HelpTip k={r.k} text={r.help} />
          if (r.kind === 'heading' || r.kind === 'subheading') {
            return <tr key={key} className={`fs-${r.kind}`}><th colSpan={showPrior ? 4 : 2} scope="rowgroup"><span className="label-with-help">{r.label}{help}</span></th></tr>
          }
          if (r.kind === 'note') {
            return <tr key={key} className="fs-note"><td colSpan={showPrior ? 4 : 2}>{r.label}</td></tr>
          }
          const canOpen = r.kind === 'line' && r.accounts?.length > 0
          const isOpen = canOpen && (open[key] ?? openAll)
          const change = showPrior && r.prior != null && r.amount != null ? Math.round((r.amount - r.prior) * 100) / 100 : null
          return (
            <Fragment key={key}>
              <tr className={`fs-${r.kind}`}>
                <td>
                  {canOpen ? (
                    <button type="button" className="fs-toggle" aria-expanded={isOpen} onClick={() => toggle(key)}>
                      {isOpen ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                      {r.label}
                    </button>
                  ) : r.kind === 'line' && r.link ? <Link className="fs-link" to={r.link}>{r.label}</Link> : <span className="label-with-help">{r.label}{help}</span>}
                  {r.extra && <span className="fs-extra">{r.extra}</span>}
                </td>
                <td className="num">{acct(r.amount)}</td>
                {showPrior && <td className="num">{acct(r.prior)}</td>}
                {showPrior && <td className="num fs-change">{change == null ? '' : acct(change)}</td>}
              </tr>
              {isOpen && r.accounts.map((a) => (
                <tr key={`${key}-${a.code}`} className="fs-detail">
                  <td><Link className="fs-link" to={glLink(a.code)}>{a.code} · {a.name}</Link></td>
                  <td className="num">{acct(a.amount)}</td>
                  {showPrior && <td className="num">{acct(a.prior)}</td>}
                  {showPrior && <td className="num fs-change">{a.prior == null ? '' : acct(Math.round((a.amount - a.prior) * 100) / 100)}</td>}
                </tr>
              ))}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

/** Download statement rows as CSV (with account detail). */
export function exportRowsCsv(rows, { name, currentLabel, priorLabel }) {
  const head = ['Line', currentLabel, ...(priorLabel ? [priorLabel] : [])]
  const out = []
  rows.forEach((r) => {
    if (r.kind === 'heading' || r.kind === 'subheading') { out.push([r.label.toUpperCase()]); return }
    if (r.kind === 'note') return
    out.push([r.label, r.amount != null ? Number(r.amount).toFixed(2) : '', ...(priorLabel ? [r.prior != null ? Number(r.prior).toFixed(2) : ''] : [])])
    ;(r.accounts || []).forEach((a) => out.push([`   ${a.code} ${a.name}`, Number(a.amount).toFixed(2), ...(priorLabel ? [a.prior != null ? Number(a.prior).toFixed(2) : ''] : [])]))
  })
  const csv = [head, ...out].map((a) => a.map((x) => `"${String(x ?? '').replaceAll('"', '""')}"`).join(',')).join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `${name}_${localToday()}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/** Statement lines for one section: [{kind:'line', label, amount, prior, accounts}]. */
export const sectionLines = (section) => (section?.lines || []).map((l) => ({
  kind: 'line', label: l.name || l.code, amount: l.amount, prior: l.prior, accounts: l.accounts,
  k: l.unclosed ? 'currentEarnings' : undefined,
}))

/** Income statement rows for StatementTable (multi-step, GAAP). */
export function incomeRows(data) {
  const sec = Object.fromEntries(data.sections.map((s) => [s.key, s]))
  const f = data.figures
  const pf = data.prior_figures || {}
  const r = []
  r.push({ kind: 'heading', label: 'Revenue', k: 'netRevenue' })
  r.push(...sectionLines(sec.revenue), ...sectionLines(sec.service_revenue))
  r.push({ kind: 'subtotal', label: 'Net revenue', amount: f.net_revenue, prior: pf.net_revenue })
  // Products, services or both: GAAP shows the two costs apart under
  // "Cost of revenue" when a business sells both.
  const both = data.sells?.products && data.sells?.services
  const cost = both ? { label: 'Cost of revenue', k: 'costOfRevenue' }
    : data.sells?.services ? { label: 'Cost of services', k: 'costOfServices' } : { label: 'Cost of goods sold', k: 'cogs' }
  r.push({ kind: 'heading', label: cost.label, k: cost.k })
  r.push(...sectionLines(sec.cogs), ...sectionLines(sec.cost_of_services))
  if (!sec.cogs.lines.length && !sec.cost_of_services.lines.length) r.push({ kind: 'note', label: 'None' })
  r.push({ kind: 'subtotal', label: `Total ${cost.label.toLowerCase()}`, amount: f.cost_of_revenue, prior: pf.cost_of_revenue })
  r.push({ kind: 'total', label: 'Gross profit', k: 'grossProfit', amount: f.gross_profit, prior: pf.gross_profit,
    extra: f.gross_margin_pct != null ? `${f.gross_margin_pct}% margin` : '' })
  r.push({ kind: 'heading', label: 'Operating expenses', k: 'operatingExpenses' })
  r.push(...sectionLines(sec.operating_expense))
  r.push({ kind: 'subtotal', label: 'Total operating expenses', amount: f.operating_expenses, prior: pf.operating_expenses })
  r.push({ kind: 'total', label: 'Operating income', k: 'operatingIncome', amount: f.operating_income, prior: pf.operating_income })
  const hasOther = sec.other_income.lines.length || sec.other_expense.lines.length
  if (hasOther) {
    r.push({ kind: 'heading', label: 'Other income and expenses', k: 'otherIncomeExpense' })
    r.push(...sectionLines(sec.other_income))
    // Other expenses reduce income, so they show in brackets here.
    r.push(...sectionLines(sec.other_expense).map((l) => ({ ...l, amount: -l.amount, prior: l.prior == null ? null : -l.prior,
      accounts: (l.accounts || []).map((a) => ({ ...a, amount: -a.amount, prior: a.prior == null ? null : -a.prior })) })))
    r.push({ kind: 'subtotal', label: 'Total other income (expenses)', amount: Math.round((f.other_income - f.other_expenses) * 100) / 100,
      prior: data.prior_figures ? Math.round((pf.other_income - pf.other_expenses) * 100) / 100 : null })
  }
  r.push({ kind: 'subtotal', label: 'Income before income taxes', k: 'incomeBeforeTax', amount: f.income_before_tax, prior: pf.income_before_tax })
  if (sec.income_tax.lines.length) {
    r.push(...sectionLines(sec.income_tax))
  }
  r.push({ kind: 'grand', label: 'Net income', k: 'netIncome', amount: f.net_income, prior: pf.net_income })
  return r.map((x) => (data.prior_figures ? x : { ...x, prior: null }))
}

const BS_HELP = {
  current_asset: 'currentAssets', noncurrent_asset: 'nonCurrentAssets', current_liability: 'currentLiabilities',
  longterm_liability: 'longTermLiabilities',
}

/** Balance sheet rows for StatementTable (classified, GAAP). */
export function balanceRows(data) {
  const r = []
  const group = (part, totalLabel) => {
    part.sections.forEach((s) => {
      if (!s.lines.length && !['current_asset', 'current_liability'].includes(s.key)) return
      r.push({ kind: 'subheading', label: s.title, k: BS_HELP[s.key] })
      r.push(...sectionLines(s))
      if (!s.lines.length) r.push({ kind: 'note', label: 'None' })
      r.push({ kind: 'subtotal', label: `Total ${s.title.toLowerCase()}`, amount: s.total, prior: s.prior_total })
    })
    r.push({ kind: 'total', label: totalLabel, amount: part.total, prior: part.prior_total })
  }
  r.push({ kind: 'heading', label: 'Assets' })
  group(data.assets, 'Total assets')
  r.push({ kind: 'heading', label: 'Liabilities and equity' })
  group(data.liabilities, 'Total liabilities')
  r.push({ kind: 'subheading', label: 'Equity', k: 'equity' })
  data.equity.sections.forEach((s) => r.push(...sectionLines(s)))
  r.push({ kind: 'subtotal', label: 'Total equity', amount: data.equity.total, prior: data.equity.prior_total })
  r.push({ kind: 'grand', label: 'Total liabilities and equity', amount: data.total_liabilities_and_equity, prior: data.prior_total_liabilities_and_equity })
  // The grand total of assets is shown double-underlined too.
  return r.map((x) => (x.label === 'Total assets' ? { ...x, kind: 'grand' } : x))
}
